/** What this task was asked to arrive with, and what has.
 *
 *  Three people read this and each needs something different from it. The Owner sets
 *  the list. The person doing the work needs to know what to send, and uploads against
 *  a named row rather than into a pile. The approver needs to see, before deciding,
 *  which of the things asked for did not turn up — which is the whole reason the field
 *  exists, since today they find that out while already deciding.
 *
 *  A **required** document blocks submission; an optional one is a checklist. That
 *  difference is why the tick is per document rather than per task.
 */
import { useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, upload, type Task, type TaskDocument } from '@/api/client'
import { ErrorNote } from '@/components/ui'

export function DocumentChecklist({ task, canAttach, canEdit }: {
  task: Task
  canAttach: boolean
  canEdit: boolean
}) {
  const qc = useQueryClient()
  const [err, setErr] = useState<unknown>(null)
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState<{ name: string; required: boolean }[]>([])
  const [busy, setBusy] = useState('')
  const input = useRef<HTMLInputElement>(null)
  const target = useRef<string>('')

  const q = useQuery({
    queryKey: ['documents', task.task_id],
    queryFn: () => api.get<TaskDocument[]>(`/tasks/${task.task_id}/documents`),
  })

  const refresh = () => {
    setErr(null)
    qc.invalidateQueries({ queryKey: ['documents', task.task_id] })
    qc.invalidateQueries({ queryKey: ['attachments', task.task_id] })
  }

  const save = useMutation({
    mutationFn: (rows: { name: string; required: boolean }[]) =>
      api.put<TaskDocument[]>(`/tasks/${task.task_id}/documents`,
                              rows.filter((r) => r.name.trim())),
    onSuccess: () => { setEditing(false); refresh() },
    onError: setErr,
  })

  const send = async (file: File) => {
    setBusy(target.current)
    try {
      await upload(`/tasks/${task.task_id}/attachments`, file, '', target.current)
      refresh()
    } catch (e) {
      setErr(e)
    } finally {
      setBusy('')
      target.current = ''
    }
  }

  // A 404 is "not yours to see", same as the attachments panel.
  if (q.error) return null
  const docs = q.data ?? []
  if (docs.length === 0 && !canEdit) return null

  const missing = docs.filter((d) => d.required && !d.provided)

  const startEditing = () => {
    setDraft(docs.length
      ? docs.map((d) => ({ name: d.name, required: d.required }))
      : [{ name: '', required: true }])
    setEditing(true)
  }

  return (
    <div className="px-5 py-3 border-b border-rule">
      <div className="flex items-center gap-2">
        <span className="text-[11px] font-semibold uppercase tracking-wider text-ink-3">
          Documents asked for — {docs.length}
        </span>
        {missing.length > 0 && (
          <span className="text-[11px] text-rej-ink">
            {missing.length} still needed
          </span>
        )}
        <span className="flex-1" />
        {canEdit && !editing && (
          <button type="button" className="btn btn-sm" onClick={startEditing}>
            {docs.length ? 'Change' : 'Ask for a document'}
          </button>
        )}
      </div>

      {err ? <div className="mt-2"><ErrorNote error={err} /></div> : null}

      {editing ? (
        <div className="mt-2 space-y-1.5">
          {draft.map((d, i) => (
            <div key={i} className="flex items-center gap-2">
              <input className="field text-[12.5px] flex-1" value={d.name}
                     maxLength={120} placeholder="GST challan"
                     onChange={(e) => setDraft(draft.map(
                       (x, j) => j === i ? { ...x, name: e.target.value } : x))} />
              <label className="flex items-center gap-1 text-[11.5px] text-ink-2">
                <input type="checkbox" checked={d.required}
                       onChange={(e) => setDraft(draft.map(
                         (x, j) => j === i
                           ? { ...x, required: e.target.checked } : x))} />
                required
              </label>
              <button type="button" className="text-ink-3 hover:text-ink px-1"
                      onClick={() => setDraft(draft.filter((_, j) => j !== i))}>
                ×
              </button>
            </div>
          ))}
          <div className="flex gap-2 pt-1">
            <button type="button" className="btn btn-sm"
                    onClick={() => setDraft([...draft,
                                             { name: '', required: true }])}>
              Add another
            </button>
            <span className="flex-1" />
            <button type="button" className="btn btn-sm"
                    onClick={() => setEditing(false)}>Cancel</button>
            <button type="button" className="btn btn-sm btn-primary"
                    disabled={save.isPending}
                    onClick={() => save.mutate(draft)}>
              {save.isPending ? 'Saving…' : 'Save'}
            </button>
          </div>
          <p className="text-[11px] text-ink-3">
            A <b>required</b> document has to be attached before the task can be
            submitted. An optional one is only a reminder.
          </p>
        </div>
      ) : (
        <ul className="mt-2 space-y-1">
          {docs.map((d) => (
            <li key={d.document_id} className="flex items-center gap-2 text-[12.5px]">
              <span className={d.provided ? 'text-done-ink' : 'text-ink-3'}>
                {d.provided ? '✓' : '○'}
              </span>
              <span className={d.provided ? '' : 'text-ink-2'}>{d.name}</span>
              {d.required && !d.provided && (
                <span className="text-[10.5px] text-rej-ink">required</span>
              )}
              <span className="flex-1" />
              {canAttach && !d.provided && (
                <button type="button" className="btn btn-sm"
                        disabled={!!busy}
                        onClick={() => {
                          target.current = d.document_id
                          input.current?.click()
                        }}>
                  {busy === d.document_id ? 'Adding…' : 'Attach'}
                </button>
              )}
            </li>
          ))}
        </ul>
      )}

      <input ref={input} type="file" className="hidden"
             accept="image/*,application/pdf"
             onChange={(e) => {
               const f = e.target.files?.[0]
               // Cleared so choosing the same file twice fires onChange again.
               e.target.value = ''
               if (f) send(f)
             }} />
    </div>
  )
}
